/**
 * Customer business roles (DynamoDB customer-users.roles).
 * See docs/customer-portal-business-roles.md.
 */

export const ROLE_IT = "IT";
export const ROLE_VIEWER = "Viewer";
export const ROLE_OPERATIONS = "Operations";
export const ROLE_BILLING = "Billing";
export const ROLE_UPLOADER = "Uploader";

/** Canonical options for Add User / profile editors (Clinician retired). */
export const CUSTOMER_ROLE_OPTIONS = [
  { id: ROLE_IT, label: "IT Admin" },
  { id: ROLE_VIEWER, label: "Viewer" },
  { id: ROLE_OPERATIONS, label: "Operations" },
  { id: ROLE_BILLING, label: "Billing" },
  { id: ROLE_UPLOADER, label: "Uploader" },
];

export const CUSTOMER_ROLE_IDS = CUSTOMER_ROLE_OPTIONS.map((r) => r.id);

/** Map legacy Clinician → Viewer; drop unknown ids. */
export function normalizeCustomerRoles(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    if (typeof item !== "string" || !item.trim()) continue;
    let id = item.trim();
    if (id.toLowerCase() === "clinician") id = ROLE_VIEWER;
    const known = CUSTOMER_ROLE_IDS.find((k) => k.toLowerCase() === id.toLowerCase());
    if (!known || seen.has(known)) continue;
    seen.add(known);
    out.push(known);
  }
  return out;
}

export function hasAnyCustomerRole(roles, required) {
  const have = new Set(normalizeCustomerRoles(roles));
  return (required || []).some((r) => have.has(r));
}

export const ROLE_HELP_SECTIONS = [
  {
    id: ROLE_IT,
    title: "IT Admin",
    allows: [
      "Create, modify, and delete user accounts for this organization (Organization → Users).",
      "Invite / import users, reset password, and resend invites where available.",
      "Organization → Software (download the supported Smart Agent for site install).",
    ],
    denies: [
      "Does not by itself grant Slide Lookup, Upload, Upload Activity, Reports, or upload APIs.",
    ],
  },
  {
    id: ROLE_VIEWER,
    title: "Viewer",
    allows: [
      "Slides → Lookup.",
      "Open AIxMed Cytology Viewer from slide detail when a viewer URL is available.",
    ],
    denies: [
      "Does not grant user administration, Upload / Upload Activity pages, Reports, or upload APIs.",
      "Former Clinician role is treated as Viewer.",
    ],
  },
  {
    id: ROLE_OPERATIONS,
    title: "Operations",
    allows: [
      "Slides → Upload and Slides → Upload Activity pages.",
      "Organization → Software (download the supported Smart Agent).",
      "Slide detail (metadata) for operational follow-up.",
    ],
    denies: [
      "Does not authorize upload APIs by itself — pair with Uploader for browser or agent uploads.",
      "Does not grant Cytology Viewer launch, Lookup, Reports, or user administration.",
    ],
  },
  {
    id: ROLE_BILLING,
    title: "Billing",
    allows: ["Reports page (list and download customer reports)."],
    denies: [
      "Does not grant Slides pages, Cytology Viewer, user administration, or upload APIs.",
    ],
  },
  {
    id: ROLE_UPLOADER,
    title: "Uploader",
    allows: [
      "Slide upload APIs (presigned / resume / complete), including the Windows upload agent.",
    ],
    denies: [
      "Does not by itself show Upload / Upload Activity in the portal — pair with Operations for those pages.",
    ],
  },
];
